import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import {
  Box,
  FormControlLabel,
  MenuItem,
  Popover,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Tooltip,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { Button, Typography } from "@platform/design-system";
import React, { useState } from "react";

import { usePlanOptionEditor } from "../../context/PlanOptionEditorContext";

import type { CustomHeaderGroupProps } from "ag-grid-react";

export interface EditablePlanOptionHeaderParams {
  /** Proposal plan id this option belongs to (join key for save + overrides). */
  planId: string;
  /** Display label for the current option (enum label or custom text). */
  optionLabel: string;
}

type Props = CustomHeaderGroupProps & EditablePlanOptionHeaderParams;

/**
 * Editable "Plan Option" header cell (CD-1261). Renders the current option as a
 * clickable label; clicking opens a popover with an "Edit Benefit / Enter
 * Custom Text" toggle — a dropdown of the enum options, or a free-text field —
 * mirroring the body-cell editor. Read-only columns (e.g. Incumbent/Requested)
 * render a plain label. All wiring comes from PlanOptionEditorContext so the
 * shared lib stays app-agnostic.
 */
const EditablePlanOptionHeader = (props: Props): React.JSX.Element => {
  const { planId, optionLabel } = props;
  const api = usePlanOptionEditor();
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const editable = !!api && api.isEditable(planId);
  const customText = api?.getCustomText(planId);
  // Prefer the live label from context — the static `optionLabel` param is not
  // refreshed by AG Grid after a refetch, so an edit would otherwise not render.
  const liveOptionLabel = api?.getOptionLabel(planId);
  const display = customText ?? liveOptionLabel ?? optionLabel;
  const edited = !!api && api.isEdited(planId);

  const [mode, setMode] = useState<"option" | "customText">(
    customText ? "customText" : "option",
  );
  const [draftOption, setDraftOption] = useState<string>("");
  const [draftCustomText, setDraftCustomText] = useState<string>(
    customText ?? "",
  );

  const open = (target: HTMLElement): void => {
    if (!editable) return;

    setDraftOption(api?.getOptionValue(planId) ?? "");
    setDraftCustomText(customText ?? "");
    setMode(customText ? "customText" : "option");
    setAnchorEl(target);
  };

  const handleClick = (event: React.MouseEvent<HTMLDivElement>): void => {
    open(event.currentTarget);
  };
  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>): void => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      open(event.currentTarget);
    }
  };

  const close = (): void => setAnchorEl(null);

  const save = (): void => {
    if (!api) return;

    const nextCustomText = draftCustomText.trim() || null;
    const currentOption = api.getOptionValue(planId);
    const didChange =
      mode === "customText"
        ? (nextCustomText ?? "") !== (customText ?? "")
        : (customText ? true : false) ||
          (draftOption !== "" && draftOption !== currentOption);

    if (mode === "customText") {
      api.onCustomTextChange(planId, nextCustomText);
    } else {
      if (customText) api.onCustomTextChange(planId, null);
      if (draftOption) api.onSelectOption(planId, draftOption);
    }

    if (didChange) {
      api.markEdited(planId);
    }

    close();
  };

  // Reuse the established "Edited" pencil (EditOutlinedIcon + ai.edited palette
  // colour) from the Quotes legend so an edited plan option matches every other
  // edited cell. Shown only after an edit — never as a persistent affordance on
  // blank/unedited cells — and positioned on the right.
  const editedIndicator = edited ? (
    <Tooltip title="Edited">
      <EditOutlinedIcon
        sx={{
          position: "absolute",
          right: 6,
          top: "50%",
          transform: "translateY(-50%)",
          fontSize: 14,
          color: theme.palette.ai?.edited ?? "brandPrimary.main",
        }}
      />
    </Tooltip>
  ) : null;

  if (!editable) {
    return (
      <Box
        sx={{
          position: "relative",
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <span className="ag-header-group-text">{display}</span>
        {editedIndicator}
      </Box>
    );
  }

  return (
    <>
      <Box
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        role="button"
        aria-label={`Edit plan option${display ? `: ${display}` : ""}`}
        sx={{
          position: "relative",
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
        }}
      >
        <span className="ag-header-group-text">{display || "—"}</span>
        {editedIndicator}
      </Box>

      <Popover
        open={!!anchorEl}
        anchorEl={anchorEl}
        onClose={close}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Box sx={{ p: 2, width: 320 }}>
          <Stack spacing={1.5}>
            <Typography variant="subtitle1" color="textSecondary">
              <strong>Edit Plan Option</strong>
            </Typography>

            <RadioGroup
              row
              value={mode}
              onChange={(e) =>
                setMode(e.target.value as "option" | "customText")
              }
            >
              <FormControlLabel
                value="option"
                control={<Radio size="small" />}
                label="Edit Benefit"
              />
              <FormControlLabel
                value="customText"
                control={<Radio size="small" />}
                label="Enter Custom Text"
              />
            </RadioGroup>

            {mode === "option" ? (
              <TextField
                select
                size="small"
                label="Plan Option"
                value={draftOption}
                onChange={(e) => setDraftOption(e.target.value)}
                fullWidth
              >
                {(api?.options ?? []).map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </TextField>
            ) : (
              <TextField
                size="small"
                label="Custom Text"
                placeholder="e.g. Silver, Core, Buy-Up…"
                value={draftCustomText}
                onChange={(e) => setDraftCustomText(e.target.value)}
                fullWidth
              />
            )}

            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <Button size="sm" variant="secondary" onClick={close}>
                Cancel
              </Button>
              <Button size="sm" variant="primary" onClick={save}>
                Save
              </Button>
            </Stack>
          </Stack>
        </Box>
      </Popover>
    </>
  );
};

EditablePlanOptionHeader.displayName = "EditablePlanOptionHeader";
export default EditablePlanOptionHeader;
